import { useEffect } from 'react';
import {
  useForm,
  DeepPartial,
  RegisterOptions,
  FieldErrors,
  UseFormRegister,
  FieldValues,
} from 'react-hook-form';
import { OneWayInServiceUpsertDto } from 'src/api/v2';

export type SetErrorFunction<T extends FieldValues> = (
  errors: FieldErrors<T> | ((prevErrors: FieldErrors<T>) => FieldErrors<T>),
) => void;

export type SubFormProps<T extends FieldValues> = {
  data: OneWayInServiceUpsertDto;
  updateData: (data: Partial<OneWayInServiceUpsertDto>) => void;
  errors: FieldErrors<T>;
  setErrors: SetErrorFunction<T>;
};

export type SubFormReturn<T extends { [K in keyof OneWayInServiceUpsertDto]?: any }, K extends keyof T> = {
  form: ReturnType<typeof useForm<T>>;
  registerWithRules: (field: K) => ReturnType<UseFormRegister<T>>;
  rules: Partial<Record<keyof T, RegisterOptions>>;
};

export const extractSubData = <T,>(data: OneWayInServiceUpsertDto, fields: string[]): T => {
  const subData: T = {} as T;
  fields.forEach((field) => {
    if (field in data) {
      subData[field] = data[field];
    }
  });
  return subData;
};

const useServiceSubForm = <T extends Partial<OneWayInServiceUpsertDto>>(
  defaultValues: DeepPartial<T>,
  updateData: (data: T) => void,
  incomingErrors: FieldErrors<T>,
  setErrors: SetErrorFunction<T>,
) => {
  const form = useForm<T>({
    mode: 'onBlur',
    defaultValues,
    reValidateMode: 'onBlur',
    criteriaMode: 'all',
  });

  const { formState, getValues, watch } = form;

  const { errors } = formState;

  const updateErrors = () => {
    const currentValues = getValues();
    const newErrors = { ...incomingErrors };

    // Remove errors that exist in currentValues and incomingErrors but not in form errors
    const keysToDelete = Object.keys(currentValues).filter(
      (key) => key in incomingErrors && !(key in errors),
    );

    // Add errors that exist in form errors but not in incomingErrors
    const keysToAdd = Object.keys(errors).filter((key) => !(key in incomingErrors));

    if (keysToDelete.length > 0 || keysToAdd.length > 0) {
      keysToDelete.forEach((key) => delete newErrors[key as keyof FieldErrors<T>]);
      keysToAdd.forEach(
        (key) => (newErrors[key as keyof FieldErrors<T>] = errors[key as keyof FieldErrors<T>]),
      );
      setErrors(newErrors);
    }
  };

  useEffect(() => {
    const data = getValues();
    for (const errorField of Object.keys(incomingErrors)) {
      if (errorField in data) {
        form.trigger(errorField as any);
      }
    }
    //TODO:Ting slutter å funke dersom man legger til manglende dependencies her, bør nok sees på
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingErrors]);

  useEffect(() => {
    const currentValues = getValues();
    updateData(currentValues);
    updateErrors();
    //TODO: Samme som over
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch(), formState]);

  return { form };
};

export default useServiceSubForm;
